import React from 'react';

import { ChallengeDescriptionSectionRequest } from 'src/types/shared/Challenge';
import { FILE_TYPES } from 'lib/src/constants/fileMimeTypes';

import ActionButton from 'lib/src/components/button/ActionButton';
import ButtonRow from 'lib/src/components/button/ButtonRow';
import FormField from 'lib/src/components/form/FormField';
import TextInput from '@components/form/TextInput';
import useFieldValidation from '@components/form/hooks/useFieldValidation';
import FilePicker from '@components/filepicker/FilePicker';

const { JPG, PNG } = FILE_TYPES;

const ImageInput: React.FC<ImageInputProps> = ({
    value,
    onChange,
    handleDelete,
    handleMoveSection,
    index,
    API_URL,
    S3_URL,
    shouldShowSortButton,
    canMoveUp,
    canMoveDown,
}) => {
    const [error] = useFieldValidation({
        name: `descriptionSections[${index}]`,
        value,
    });

    return (
        <div className="db-input image" key={index || undefined}>
            <label>Image Input</label>
            <div className={`input-container ${error ? 'error' : ''}`}>
                <FormField name={`descriptionSections[${index}]`} error={error}>
                    <label htmlFor={`imageInput-title-${index}`}>
                        Section title
                        <TextInput
                            name={`imageInput-title-${index}`}
                            value={value.title}
                            placeholder="Section title"
                            onChange={(name, val) => _onChange(name, [val])}
                        />
                    </label>
                    <label htmlFor={`imageInput-subtitle-${index}`}>
                        Image Subtitle
                        <TextInput
                            name={`imageInput-subtitle-${index}`}
                            value={value.contents[0].title}
                            placeholder="Section title"
                            onChange={(name, val) => _onChange(name, [val])}
                        />
                    </label>
                    <label htmlFor={`imageInput-${index}`}>
                        Image file
                        <FilePicker
                            apiURL={API_URL}
                            storageURL={S3_URL}
                            name={`imageInput-${index}`}
                            value={
                                value.contents[0].content === ''
                                    ? null
                                    : [value.contents[0].content]
                            }
                            onChange={_onChange}
                            maxFiles={1}
                            acceptedTypes={`${JPG}, ${PNG}`}
                        />
                    </label>
                    <ButtonRow>
                        {!!handleMoveSection && (
                            <>
                                <ActionButton
                                    type="button"
                                    onClick={() => handleMoveSection(index, -1)}
                                    color="grey"
                                    icon="chevron-up"
                                    disabled={!canMoveUp}
                                >
                                    Move up
                                </ActionButton>
                                <ActionButton
                                    type="button"
                                    onClick={() => handleMoveSection(index, 1)}
                                    color="grey"
                                    icon="chevron-down"
                                    disabled={!canMoveDown}
                                >
                                    Move down
                                </ActionButton>
                            </>
                        )}
                        {!!handleDelete && (
                            <ActionButton
                                type="button"
                                color="red"
                                onClick={() => handleDelete(index)}
                            >
                                Delete Section
                            </ActionButton>
                        )}
                    </ButtonRow>
                </FormField>
            </div>
        </div>
    );

    function _onChange(name: string, content: string[] | null) {
        if (content === null)
            onChange(index, {
                ...value,
                contents: [{ ...value.contents[0], content: '' }],
            });
        else if (name === `imageInput-${index}`)
            onChange(index, {
                ...value,
                contents: [{ ...value.contents[0], content: content[0] || '' }],
            });
        else if (name === `imageInput-title-${index}`)
            onChange(index, { ...value, title: content[0] });
        else if (name === `imageInput-subtitle-${index}`)
            onChange(index, { ...value, contents: [{ ...value.contents[0], title: content[0] }] });
    }
};

interface ImageInputProps {
    value: ChallengeDescriptionSectionRequest;
    onChange: (index: number, newData: ChallengeDescriptionSectionRequest) => void;
    handleDelete?: (index: number) => void;
    handleMoveSection?: (index: number, moveBy: number) => void;
    index: number;
    API_URL: string;
    S3_URL: string;
    shouldShowSortButton: boolean;
    canMoveUp: boolean;
    canMoveDown: boolean;
}

export default ImageInput;
